import type { IDBPDatabase, IDBPTransaction } from 'idb';
import { openFenixDatabase, storageFailure, validDatasetControl } from './fenix-db.ts';
import type { FenixSchema } from './fenix-db.ts';
import { canonical } from './canonical.ts';
import { compareWeightInstants, correctWeight, failure, inPeriod, validPeriod, validWeight } from '../domain/progress.ts';
import type { Period, ProgressResult, WeightObservation } from '../domain/progress.ts';
import { validId, validRevision } from '../domain/training.ts';
import type { ProgressContext, ProgressRepository, WeightCommand, WeightCommit, WeightView } from '../ports/progress-repository.ts';

const stores = ['control', 'progress_weights', 'progress_weight_history', 'progress_receipts'] as const;
type Tx<M extends 'readonly' | 'readwrite'> = IDBPTransaction<FenixSchema, typeof stores, M>;
export type ProgressFault = 'after-current' | 'after-history' | 'after-receipt' | 'after-control' | 'lost-ack' | 'reread-abort';
export class ProgressLabFaults {
  next: ProgressFault | null = null;
  failNextRead = false;
  take() { const fault = this.next; this.next = null; return fault; }
}
interface Receipt { operationId: string; datasetId: string; fingerprint: string; observation: WeightObservation }
const range = (dataset: string) => IDBKeyRange.bound([dataset], [dataset, []]);
export class IndexedProgress implements ProgressRepository {
  private available = true;
  constructor(private db: IDBPDatabase<FenixSchema>, private faults = new ProgressLabFaults()) {
    db.addEventListener('versionchange', () => this.close());
    db.addEventListener('close', () => { this.available = false; });
  }
  close() { this.available = false; this.db.close(); }
  private async tx<M extends 'readonly' | 'readwrite', T>(mode: M, action: (tx: Tx<M>) => Promise<ProgressResult<T>>): Promise<ProgressResult<T>> {
    if (!this.available) return failure('STORAGE_UNAVAILABLE');
    if (mode === 'readonly' && this.faults.failNextRead) {
      this.faults.failNextRead = false; return failure('TRANSACTION_ABORTED');
    }
    let tx: Tx<M> | undefined;
    try {
      tx = this.db.transaction(stores, mode, { durability: 'strict' });
      void tx.done.catch(() => {});
      const result = await action(tx);
      if (!result.ok) { tx.abort(); await tx.done.catch(() => {}); return result; }
      await tx.done;
      return result;
    } catch (error) {
      if (tx) { try { tx.abort(); } catch { /* already finished */ } await tx.done.catch(() => {}); }
      return storageFailure(error);
    }
  }
  private async control<M extends 'readonly' | 'readwrite'>(tx: Tx<M>, expected?: ProgressContext) {
    const control = await tx.objectStore('control').get('control');
    if (!validDatasetControl(control) || control.recoveryDatasetId !== null) return failure('CORRUPTION_DETECTED');
    if (control.restoreFence !== null) return failure('RESTORE_IN_PROGRESS');
    if (expected && (expected.datasetId !== control.activeDatasetId || expected.controlEpoch !== control.controlEpoch)) return failure('DATASET_STALE');
    return { ok: true as const, value: control };
  }
  readContext(): Promise<ProgressResult<ProgressContext>> {
    return this.tx('readonly', async tx => {
      const c = await this.control(tx);
      return c.ok ? { ok: true, value: { datasetId: c.value.activeDatasetId, controlEpoch: c.value.controlEpoch } } : c;
    });
  }
  listWeights(period: Period): Promise<ProgressResult<readonly WeightView[]>> {
    if (!validPeriod(period)) return Promise.resolve(failure('VALIDATION_ERROR'));
    return this.tx('readonly', async tx => {
      const c = await this.control(tx); if (!c.ok) return c;
      const context = { datasetId: c.value.activeDatasetId, controlEpoch: c.value.controlEpoch };
      const output: WeightView[] = [];
      let cursor = await tx.objectStore('progress_weights').openCursor(range(context.datasetId));
      while (cursor) {
        const row = cursor.value;
        if (row.datasetId !== context.datasetId || !validWeight(row.record) || cursor.key[1] !== row.record.observationId) return failure('CORRUPTION_DETECTED');
        if (inPeriod(row.record.localDate, period)) output.push({ observation: row.record, context });
        cursor = await cursor.continue();
      }
      output.sort((a, b) => b.observation.localDate.localeCompare(a.observation.localDate)
        || compareWeightInstants(b.observation.recordedInstant,a.observation.recordedInstant)
        || a.observation.observationId.localeCompare(b.observation.observationId));
      return { ok: true, value: output };
    });
  }
  readWeight(id: string): Promise<ProgressResult<WeightView>> {
    if (!validId(id)) return Promise.resolve(failure('VALIDATION_ERROR'));
    return this.tx('readonly', async tx => {
      const c = await this.control(tx); if (!c.ok) return c;
      const context = { datasetId: c.value.activeDatasetId, controlEpoch: c.value.controlEpoch };
      const row = await tx.objectStore('progress_weights').get([context.datasetId, id]);
      if (!row) return failure('NOT_FOUND');
      if (row.datasetId !== context.datasetId || !validWeight(row.record) || row.record.observationId !== id) return failure('CORRUPTION_DETECTED');
      return { ok: true, value: { observation: row.record, context } };
    });
  }
  readWeightHistory(id: string): Promise<ProgressResult<readonly WeightObservation[]>> {
    if (!validId(id)) return Promise.resolve(failure('VALIDATION_ERROR'));
    return this.tx('readonly', async tx => {
      const c = await this.control(tx); if (!c.ok) return c;
      const dataset = c.value.activeDatasetId, output: WeightObservation[] = [];
      let cursor = await tx.objectStore('progress_weight_history').openCursor(IDBKeyRange.bound([dataset, id], [dataset, id, []]));
      while (cursor) {
        const row = cursor.value;
        if (row.datasetId !== dataset || !validWeight(row.record) || row.record.observationId !== id || row.record.revision !== cursor.key[2]) return failure('CORRUPTION_DETECTED');
        output.push(row.record); cursor = await cursor.continue();
      }
      return { ok: true, value: output };
    });
  }
  recordWeight(command: WeightCommand) { return this.write(command, 'record'); }
  correctWeight(command: WeightCommand) { return this.write(command, 'correct'); }
  private async write(input: WeightCommand, action: 'record' | 'correct'): Promise<ProgressResult<WeightCommit>> {
    if (!input || !validId(input.operationId) || !input.context || !validId(input.context.datasetId)
      || !Number.isSafeInteger(input.context.controlEpoch) || input.context.controlEpoch < 0
      || !validWeight(input.observation) || (action === 'record' ? input.expectedRevision !== 0 || input.observation.revision !== 1 : !validRevision(input.expectedRevision))) return failure('VALIDATION_ERROR');
    const command = structuredClone(input), fingerprint = canonical({ kind: 'progress-weight', action, command });
    let postCommit: ProgressFault | null = null;
    const result = await this.tx<'readwrite', WeightCommit>('readwrite', async tx => {
      const checked = await this.control(tx, command.context); if (!checked.ok) return checked;
      const control = checked.value, dataset = control.activeDatasetId;
      const receiptRow = await tx.objectStore('progress_receipts').get([dataset, command.operationId]);
      if (receiptRow) {
        const receipt = receiptRow.record as Receipt;
        if (!receipt || receiptRow.datasetId !== dataset || receipt.datasetId !== dataset || receipt.operationId !== command.operationId
          || !validWeight(receipt.observation) || typeof receipt.fingerprint !== 'string') return failure('CORRUPTION_DETECTED');
        if (receipt.fingerprint !== fingerprint) return failure('INVARIANT_CONFLICT');
        return { ok: true, value: { status: 'REPLAYED', observation: receipt.observation } };
      }
      const current = await tx.objectStore('progress_weights').get([dataset, command.observation.observationId]);
      if (action === 'record' && current) return failure('INVARIANT_CONFLICT');
      if (action === 'correct') {
        if (!current) return failure('NOT_FOUND');
        if (current.datasetId !== dataset || !validWeight(current.record) || current.record.observationId !== command.observation.observationId) return failure('CORRUPTION_DETECTED');
        const corrected = correctWeight(current.record, command.observation, command.expectedRevision);
        if (!corrected.ok) return corrected;
      }
      if (!Number.isSafeInteger(control.datasetRevision + 1)) return failure('INVARIANT_CONFLICT');
      const fault = this.faults.take();
      const checkpoint = (point: ProgressFault) => { if (fault === point) throw new DOMException('Synthetic progress abort', 'AbortError'); };
      const row = { datasetId: dataset, record: command.observation };
      await tx.objectStore('progress_weights').put(row, [dataset, command.observation.observationId]); checkpoint('after-current');
      await tx.objectStore('progress_weight_history').add(row, [dataset, command.observation.observationId, command.observation.revision]); checkpoint('after-history');
      await tx.objectStore('progress_receipts').add({ datasetId: dataset, record: { operationId: command.operationId, datasetId: dataset, fingerprint, observation: command.observation } }, [dataset, command.operationId]); checkpoint('after-receipt');
      await tx.objectStore('control').put({ ...control, datasetRevision: control.datasetRevision + 1 }); checkpoint('after-control');
      postCommit = fault;
      return { ok: true, value: { status: 'APPLIED', observation: command.observation } };
    });
    if (result.ok && postCommit === 'reread-abort') this.faults.failNextRead = true;
    return result.ok && postCommit === 'lost-ack' ? failure('STORAGE_UNAVAILABLE') : result;
  }
}
export async function openProgress(name: string, faults = new ProgressLabFaults()): Promise<ProgressResult<IndexedProgress>> {
  const opened = await openFenixDatabase(name);
  return opened.ok ? { ok: true, value: new IndexedProgress(opened.value, faults) } : opened;
}
